import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  model,
  viewChildren,
} from '@angular/core';

export interface SegmentedOption {
  readonly value: string;
  readonly label: string;
}

/**
 * Gruppo di opzioni esclusive (`role="radiogroup"`). Dalla tastiera: Tab entra sull'opzione
 * scelta, le frecce si spostano e scelgono, Home e Fine vanno agli estremi.
 */
@Component({
  selector: 'bs-segmented',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex max-w-full' },
  template: `
    <div
      role="radiogroup"
      [attr.aria-label]="label()"
      [attr.data-testid]="testId()"
      class="inline-flex max-w-full flex-wrap gap-1 rounded-2xl bg-stone-100 p-1.5 dark:bg-stone-800"
    >
      @for (option of options(); track option.value; let index = $index) {
        <button
          #option
          type="button"
          role="radio"
          [attr.aria-checked]="option.value === value()"
          [attr.tabindex]="index === focusIndex() ? 0 : -1"
          [attr.data-value]="option.value"
          [disabled]="disabled()"
          [class]="option.value === value() ? activeClass : idleClass"
          (click)="choose(option.value)"
          (keydown)="onKeydown($event)"
        >
          {{ option.label }}
        </button>
      }
    </div>
  `,
})
export class SegmentedComponent {
  readonly options = input.required<readonly SegmentedOption[]>();
  readonly value = model<string | null>(null);
  /** Nome accessibile del gruppo, per esempio "Filtra per account". */
  readonly label = input.required<string>();
  readonly disabled = input(false);
  readonly testId = input<string | null>(null);

  private readonly buttons = viewChildren<ElementRef<HTMLButtonElement>>('option');

  private readonly base =
    'h-9 max-w-full truncate rounded-xl px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50';
  protected readonly activeClass = `${this.base} bg-white text-ink shadow-sm dark:bg-stone-950 dark:text-stone-50`;
  protected readonly idleClass = `${this.base} text-stone-600 hover:text-ink dark:text-stone-300 dark:hover:text-stone-50`;

  protected readonly focusIndex = computed(() =>
    Math.max(
      0,
      this.options().findIndex((option) => option.value === this.value()),
    ),
  );

  protected choose(value: string): void {
    this.value.set(value);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const options = this.options();
    if (options.length === 0) {
      return;
    }
    const current = this.focusIndex();
    let next: number | null = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % options.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + options.length) % options.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = options.length - 1;
        break;
    }
    if (next !== null) {
      event.preventDefault();
      this.choose(options[next].value);
      this.buttons()[next]?.nativeElement.focus();
    }
  }
}
